import { PlayerApiPayload, PlayerSettings, PlayerUser } from '@foundation-player/player-api';
import { Client, Environment } from '@foundation-player/runtime';
import type { LoaderConfig } from '@foundation-player/types';

/**
 * Maps a PlayerApiPayload to a LoaderConfig
 *
 * @remarks
 * This function must be called before creating the player to generate a LoaderConfig
 * The playerElementOrId and videoElementOrId fields are not included and have to be set separately when creating the player.
 */
export function mapToLoaderConfig(
  playerApiPayload: PlayerApiPayload,
  playerElementOrId: string | HTMLElement,
  videoElementOrId?: string | HTMLVideoElement,
): LoaderConfig {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!playerApiPayload.playerClient) {
    throw new Error('Client is required to transform to loader config! Missing "client" in playerApiPayload');
  }

  const remoteConfigRegex = /html5\/([a-z]*)\/?([a-z]*(?:\/[a-z]*)?)/;
  const match = remoteConfigRegex.exec(playerApiPayload.playerClient.identifier || '');
  const client = match ? match[1] : undefined;
  const scope = match ? match[2] : undefined;

  const yospaceQueryParameters = (playerApiPayload.playerContent.modules?.yospace?.queryParameters || []).reduce(
    (dict, parameter) => {
      const [key, value] = parameter.split('=');
      dict.set(key, value);
      return dict;
    },
    new Map<string, string>(),
  );

  return {
    playerElementOrId,
    videoElementOrId,
    client: client as Client,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    scope: scope as any,
    environment: mapEnvironment(playerApiPayload.playerClient.clientEnvironment),
    consentString: playerApiPayload.playerUser.consent,
    pluginConfigs: {
      'foundation.plugin.npawAnalytics': {
        appVersion: playerApiPayload.playerClient.appVersion,
        userId: playerApiPayload.playerUser.userId,
        userType: playerApiPayload.playerUser.userType,
      },
      'foundation.plugin.heartbeat': {
        offer: playerApiPayload.playerClient.offer,
        videoService: playerApiPayload.playerClient.videoService,
        payStatus: mapHeartbeatPayStatus(playerApiPayload.playerUser.userType),
        userStatus: mapHeartbeatUserStatus(playerApiPayload.playerUser.userType),
      },
      'foundation.plugin.nielsen': {
        commentPath: playerApiPayload.playerContent.modules?.nielsen?.commentPath,
      },
      'foundation.plugin.ssai': {
        ppid: playerApiPayload.playerSettings?.modules?.yospace?.ppId,
        fwd_rpusr: yospaceQueryParameters.get('fwd_rpusr'),
        opt: convertOptInToSSAIOptIn(playerApiPayload.playerSettings?.modules?.yospace?.optIn),
      },
      'foundation.plugin.advertising': {
        smartclipConfig: {}, //playerApiPayload.settings?.modules?.smartclip?.config,
      },
    },
  };
}

function convertOptInToSSAIOptIn(optIn: boolean | undefined): 'in' | 'out' | undefined {
  if (optIn === undefined) {
    return undefined;
  }

  return optIn ? 'in' : 'out';
}

function mapHeartbeatPayStatus(input: PlayerUser['userType']): number {
  switch (input) {
    case 'PAY':
      return 11;
    case 'FREE':
      return 10;
  }

  return 0;
}

function mapHeartbeatUserStatus(input: PlayerUser['userType']): number {
  switch (input) {
    case 'PAY':
      return 5;
    case 'FREE':
      return 3;
  }

  return 0;
}

function mapEnvironment(input: PlayerSettings['environment']): Environment {
  switch (input) {
    case 'DEV':
      return Environment.Development;
    case 'PREPROD':
      return Environment.PreProduction;
    case 'PROD':
    case undefined:
      return Environment.Production;
  }

  throw new Error(`Unknown client environment: ${input}`);
}
